import { IconButton, IconButtonProps } from '@mui/material'

import { icons } from 'src/design-system'

import Iconify from './Iconify'

// ----------------------------------------------------------------------
// The × that closes a hint, and the only way to draw one.
//
// **A dismiss is in the top right corner of the thing it dismisses.** That is
// where a reader's hand goes for it, on every surface in the console and on
// every surface outside it: the map's vehicle sheet, the filter sheet, every
// dialog. An alert was the exception, and not by decision — MUI renders
// `action` in place of the close button when a call site gives both, so
// `CommandDial` put its dismiss *into* the action row to make it appear at
// all, and a refused command drew `Retry ×` as a pair of controls along the
// bottom edge. Two controls of different consequence, adjacent, at the far
// end of the block, where the one that gives up is the easier target.
//
// So the button is a component and its position is the theme's. The class it
// carries is what the `MuiAlert` override anchors: the corner, the inset it
// shares with the alert's own padding, and the room reserved beside the copy
// so a sentence never runs under it. A screen hands over a handler and a name
// for the control; there is no prop for where it goes.
//
// `size="small"` for the 32px box with the dense glyph. A dismiss is not a
// page action and it stands on a tint that already carries the sentence: it
// has to be reachable without being the first thing read.
// ----------------------------------------------------------------------

/**
 * The hook the theme positions this by.
 *
 * A class rather than a data attribute because the override composes it into
 * an MUI style slot, and a bare class is what `styleOverrides` selectors are
 * written in throughout this theme.
 */
export const HINT_DISMISS_CLASS = 'beast-hint-dismiss'

export interface HintDismissProps
  extends Omit<IconButtonProps, 'children' | 'size' | 'aria-label'> {
  /**
   * What this closes, said in full: `Dismiss Unlock`, not `Dismiss`.
   *
   * Required, because a hint is usually one of several on a screen and a
   * column of controls all announcing `Dismiss` names none of them.
   */
  label: string
}

export default function HintDismiss({
  label,
  className,
  ...other
}: HintDismissProps) {
  return (
    <IconButton
      size="small"
      aria-label={label}
      className={
        className ? `${HINT_DISMISS_CLASS} ${className}` : HINT_DISMISS_CLASS
      }
      {...other}
    >
      <Iconify icon={icons.close} size="dense" />
    </IconButton>
  )
}
